import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import LazyImage from "../components/LazyImage";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Pause, Play } from "lucide-react";

export default function TVMode() {
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(true);

    useEffect(() => {
        (async () => {
            const r = await api.get("/projects/featured", { params: { limit: 12 } });
            const list = r.data && r.data.length > 0 ? r.data : (await api.get("/projects", { params: { limit: 12 } })).data.items || [];
            setItems(list);
        })();
    }, []);

    useEffect(() => {
        if (!playing || items.length === 0) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
        return () => clearInterval(t);
    }, [playing, items]);

    const goFullscreen = useCallback(() => {
        const el = document.documentElement;
        if (!document.fullscreenElement && el.requestFullscreen) {
            el.requestFullscreen().catch(() => {});
        }
    }, []);

    if (items.length === 0) {
        return <div className="min-h-screen bg-black flex items-center justify-center text-white/60">Loading showroom…</div>;
    }
    const project = items[idx];

    return (
        <main data-testid="tv-mode-page" className="fixed inset-0 bg-black overflow-hidden text-white">
            <AnimatePresence mode="sync">
                <motion.div
                    key={project.id}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0"
                >
                    <LazyImage src={project.thumbnail_url} className="w-full h-full" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-black/30" />
                </motion.div>
            </AnimatePresence>

            {/* Controls */}
            <div className="absolute top-6 left-6 flex items-center gap-3">
                <Link to="/" data-testid="tv-exit" className="glass px-4 py-2 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase hover:text-gold">
                    <ArrowLeft size={14} /> Exit
                </Link>
                <button onClick={() => setPlaying((v) => !v)} data-testid="tv-play-toggle" className="glass p-2.5 hover:text-gold">
                    {playing ? <Pause size={14} /> : <Play size={14} />}
                </button>
                <button onClick={goFullscreen} data-testid="tv-fullscreen" className="glass px-4 py-2 text-xs tracking-[0.25em] uppercase hover:text-gold">
                    Fullscreen
                </button>
            </div>

            {/* Branding */}
            <div className="absolute top-6 right-6 glass px-5 py-3 text-xs tracking-[0.3em] uppercase">
                Tile<span className="text-gold">Sphere</span> · Showroom
            </div>

            {/* Project info */}
            <div className="absolute inset-x-0 bottom-0 p-12 md:p-20">
                <motion.div
                    key={project.id + "-info"}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-4xl"
                >
                    <div className="overline text-gold mb-4">{project.room_type} · {project.company_name}</div>
                    <h1 className="font-serif text-6xl md:text-8xl tracking-tight leading-[0.92] text-shadow-soft">{project.title}</h1>
                    <p className="mt-6 max-w-2xl text-white/75 text-lg leading-relaxed">{project.description}</p>
                </motion.div>

                <div className="mt-10 flex items-end justify-between gap-6">
                    <div className="flex gap-2 flex-1">
                        {items.map((_, i) => (
                            <div key={i} className={`h-px flex-1 transition-colors duration-700 ${i === idx ? "bg-gold" : "bg-white/20"}`} />
                        ))}
                    </div>
                    <div className="hidden md:flex flex-col items-end gap-2">
                        <div className="overline">Scan to view</div>
                        <img
                            alt="QR"
                            className="w-24 h-24 bg-white p-2"
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=000000&bgcolor=FFFFFF&data=${encodeURIComponent(window.location.origin + "/projects/" + project.slug)}`}
                        />
                    </div>
                </div>
            </div>
        </main>
    );
}
